import type {
  BillWithPayments,
  CourierReport,
  Movement,
  Payment,
  ShiftMovement,
  StockItem,
  Tab,
  TillReport,
  XReport,
  ZReport
} from '@/types'
import type { Printer } from '@/types/config'

import generateBankReceipt from './bankReceipt'
import generateBill from './bill'
import BillOrderFormatter from './billOrderFormatter'
import generateCancelTab from './cancelTab'
import type { TicketElement } from './components'
import generateCourierReport from './courierReport'
import generateKitchenNote from './kitchenNote'
import generateKitchenOrder from './kitchenOrder'
import type { KitchenOrderFormatted } from './kitchenOrderFormatter'
import generatePayInPayOut from './payInPayOut'
import generateRealTimeReport from './realTimeReport'
import generateShiftMovements from './shiftMovements'
import generateStockReport from './stockReport'
import generateTillReport from './tillReport'
import generateXReport from './xreport'
import generateZReport from './zreport'

async function render(
  parts: TicketElement[],
  scale: number = 1,
  printer?: Printer
): Promise<HTMLCanvasElement> {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!
  let height = 0
  const width = printer?.ticketWidth || 320
  for (const part of parts) {
    height += await part.height(ctx, width)
  }
  height = Math.max(printer?.ticketHeight || 0, height)
  canvas.width = width * scale
  canvas.height = height * scale
  ctx.fillStyle = 'white'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.scale(scale, scale)
  let top = 0
  for (const part of parts) {
    await part.render(top, ctx, width)
    top += await part.height(ctx, width)
  }
  return canvas
}

async function bill(
  info: BillWithPayments,
  options: any,
  scale?: number
): Promise<HTMLCanvasElement> {
  return await render(await generateBill(BillOrderFormatter.format(info), options), scale)
}

async function courierReport(
  report: CourierReport & {
    total: number
    courierName: string
  },
  scale: number
): Promise<HTMLCanvasElement> {
  return await render(generateCourierReport(report), scale)
}

async function zReport(
  report: ZReport,
  config: { company: { name: string; taxId: string; address: string } },
  scale: number
): Promise<HTMLCanvasElement> {
  return await render(generateZReport(report, config), scale)
}

async function xReport(
  report: XReport,
  config: { company: { name: string; taxId: string; address: string } },
  scale: number,
  isPreview: boolean
): Promise<HTMLCanvasElement> {
  return await render(generateXReport(report, config, isPreview), scale)
}

async function realTimeReport(
  report: ZReport,
  config: { company: { name: string; taxId: string; address: string } },
  scale: number
): Promise<HTMLCanvasElement> {
  return await render(generateRealTimeReport(report, config), scale)
}

async function tillReport(
  report: TillReport,
  tillName: string,
  scale: number
): Promise<HTMLCanvasElement> {
  return await render(generateTillReport(report, tillName), scale)
}

async function kitchenOrder(
  order: KitchenOrderFormatted,
  printer: Printer,
  scale?: number
): Promise<HTMLCanvasElement> {
  return await render(generateKitchenOrder(order, printer), scale, printer)
}

async function kitchenNote(
  courseLabel: string,
  tab: Tab,
  scale: number
): Promise<HTMLCanvasElement> {
  return await render(generateKitchenNote(courseLabel, tab), scale)
}

async function cancelTab(tab: Tab, scale: number): Promise<HTMLCanvasElement> {
  return await render(generateCancelTab(tab), scale)
}

async function payInPayOut(
  movement: Movement & {
    employee?: string
    date: Date
  },
  scale: number
): Promise<HTMLCanvasElement> {
  return await render(generatePayInPayOut(movement), scale)
}

async function shiftMovements(
  report: {
    movements: ShiftMovement[]
  },
  name: string,
  config: any,
  scale: number
): Promise<HTMLCanvasElement> {
  return await render(generateShiftMovements(report, name, config), scale)
}

async function bankReceipt(payment: Payment, scale: number): Promise<HTMLCanvasElement> {
  return await render(generateBankReceipt(payment), scale)
}

async function stockReport(items: StockItem[], scale: number) {
  return await render(generateStockReport(items), scale)
}

export default {
  bill,
  courierReport,
  zReport,
  xReport,
  realTimeReport,
  tillReport,
  kitchenOrder,
  kitchenNote,
  cancelTab,
  payInPayOut,
  shiftMovements,
  bankReceipt,
  stockReport
}
